// src/components/common/ScoreChip.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@mui/material';

/**
 * ScoreChip - Displays a score value in a styled chip component
 * Provides consistent styling for score displays across the application
 *
 * @component
 * @example
 * <ScoreChip value={4.2} color="#03DAC6" />
 * <ScoreChip value="85%" color="#FFCF44" size="small" />
 */
const ScoreChip = ({
  value,
  color,
  size = 'small',
  variant = 'filled',
  sx = {}
}) => {
  const displayValue = value !== null && value !== undefined ? value : 'N/A';

  // Default background and text colors
  const bgColor = color ? `${color}20` : 'rgba(3, 218, 198, 0.1)';
  const textColor = color || '#03DAC6';

  return (
    <Chip
      label={displayValue}
      size={size}
      variant={variant}
      sx={{
        bgcolor: variant === 'filled' ? bgColor : 'transparent',
        color: textColor,
        fontWeight: 'bold',
        border: variant === 'outlined' ? `1px solid ${textColor}` : 'none',
        ...sx
      }}
    />
  );
};

ScoreChip.propTypes = {
  /** Score value to display */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** Color for chip (affects both background and text) */
  color: PropTypes.string,
  /** Chip size */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Chip variant */
  variant: PropTypes.oneOf(['filled', 'outlined']),
  /** Additional MUI sx prop styles */
  sx: PropTypes.object
};

export default ScoreChip;
